import * as React from 'react';
import { useQuery } from 'react-query';
import { css } from '@emotion/react';

import { bundledConfig } from '~src/config/bundled-config';
import { useGlobalState } from '~src/main/context/global-state';

/**
 * We disable the compression on purpose (don't use the gzip file).
 *
 * https://github.com/GoogleCloudPlatform/gsutil/issues/480
 * https://cloud.google.com/storage/docs/transcoding
 */
const LOGO_MAP_URL = `${bundledConfig.cdnUrl}/static/shipping-logos/logo-map.json`;

type LogoMapEntry = {
  name: string;
  patterns: string[];
  pathRound32: string;
  pathRound20: string;
  pathRound30?: string;
};

type LogoMap = LogoMapEntry[];

interface ShippingLogoProps {
  className?: string;
  shippingMethod: string;
  size?: number;
}

export const ShippingLogo: React.FC<ShippingLogoProps> = ({
  className,
  shippingMethod,
  size = 30,
}) => {
  const { logoMap } = useLogoMap();

  const logo = logoMap(shippingMethod);
  if (!logo || (!logo.pathRound32 && !logo.pathRound30)) {
    return null;
  }

  const pathSelectedSize = logo.pathRound30 || logo.pathRound32;
  const path = `${bundledConfig.cdnUrl}${pathSelectedSize}`;

  return <img src={path} alt={logo.name} css={styles.img(size)} className={className} />;
};

const styles = {
  img: (size: number) =>
    css({
      height: `${size}px`,
      width: `${size}px`,
    }),
};

const fetchLogoMap = () => {
  // do not replace fetch with axios, because axios does not work with preload from index.html
  return fetch(LOGO_MAP_URL).then(response => response.json() as Promise<LogoMap>);
};

export const useLogoMap = () => {
  const globalState = useGlobalState();

  const query = useQuery('logo_map', fetchLogoMap, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const logoMap = (shippingMethod: string | undefined): LogoMapEntry | undefined => {
    if (shippingMethod === undefined || !query.data) {
      return undefined;
    }
    const logoEntry = query.data.find(({ patterns }) =>
      patterns.some(pattern => shippingMethod.match(pattern))
    );
    /*
     * In Norway Bring uses Posten logo. For the moment this is hardcoded
     * because we find no better way to do it.
     */
    if (logoEntry?.name === 'Bring' && globalState.country === 'NO') {
      return query.data.find(({ patterns }) =>
        patterns.some(pattern => 'posten'.match(pattern))
      );
    }

    return logoEntry;
  };

  return { ...query, logoMap };
};
